import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App.js';
import {
  Shield, Users, Music, TrendingUp, AlertCircle, ArrowLeft,
  Coins, CheckCircle, Clock, Calendar, RefreshCw, Sparkles, Ban, UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Floating Particles
const Particles = () => (
  <div className="particles">
    {[...Array(9)].map((_, i) => (
      <div key={i} className="particle" />
    ))}
  </div>
);

export default function AdminDashboard() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingCredits, setEditingCredits] = useState(null);
  const [newCredits, setNewCredits] = useState('');
  const [webhookData, setWebhookData] = useState(null);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [loadingWebhookDetail, setLoadingWebhookDetail] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Resilient fetch with timeout + auto-retry. During heavy backend load (batch uploads
  // + webhook bursts) or fresh-deploy cold starts, admin requests occasionally stall.
  // Rather than silently rendering zeros OR failing the whole dashboard because one call
  // stalled, we retry each endpoint independently and surface partial results.
  const fetchWithRetry = async (url, attempts = 3, timeoutMs = 25000) => {
    for (let i = 1; i <= attempts; i++) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal
        });
        clearTimeout(timer);
        if (res.ok) return res.json();
        if (i < attempts && [408, 429, 500, 502, 503, 504].includes(res.status)) {
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, i - 1)));
          continue;
        }
        throw new Error(`HTTP ${res.status}`);
      } catch (e) {
        clearTimeout(timer);
        if (i < attempts) {
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, i - 1)));
          continue;
        }
        throw e;
      }
    }
  };

  const fetchWebhooks = async () => {
    setLoadingWebhooks(true);
    try {
      const data = await fetchWithRetry(`${API_URL}/api/admin/webhooks/cyanite/recent?limit=50`);
      setWebhookData(data);
    } catch (e) {
      console.error('Failed to fetch webhooks:', e);
    }
    setLoadingWebhooks(false);
  };

  const inspectWebhook = async (webhookId) => {
    setLoadingWebhookDetail(true);
    setSelectedWebhook({ webhook_id: webhookId, _loading: true });
    try {
      const data = await fetchWithRetry(`${API_URL}/api/admin/webhooks/cyanite/${webhookId}`);
      setSelectedWebhook(data);
    } catch (e) {
      setSelectedWebhook({ webhook_id: webhookId, _error: String(e) });
    }
    setLoadingWebhookDetail(false);
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    setFetchError(null);
    // Use allSettled so a single slow endpoint doesn't nuke the whole dashboard.
    // Each succeeding endpoint updates its own slice of state; only fully-failed ones surface error.
    const [statsRes, usersRes, tracksRes] = await Promise.allSettled([
      fetchWithRetry(`${API_URL}/api/admin/stats`),
      fetchWithRetry(`${API_URL}/api/admin/users`),
      fetchWithRetry(`${API_URL}/api/admin/tracks`),
    ]);
    if (statsRes.status === 'fulfilled') setStats(statsRes.value);
    if (usersRes.status === 'fulfilled') setUsers(usersRes.value);
    if (tracksRes.status === 'fulfilled') setTracks(tracksRes.value);
    const failures = [
      statsRes.status === 'rejected' && 'stats',
      usersRes.status === 'rejected' && 'users',
      tracksRes.status === 'rejected' && 'tracks',
    ].filter(Boolean);
    if (failures.length > 0) {
      const firstErr = [statsRes, usersRes, tracksRes].find(r => r.status === 'rejected')?.reason?.message || 'unknown';
      setFetchError(`Couldn't load: ${failures.join(', ')} (${firstErr})`);
    }
    setLoading(false);
  };

  const updateCredits = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/credits?credits=${newCredits}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
        setEditingCredits(null);
        setNewCredits('');
      }
    } catch (error) {
      console.error('Failed to update credits:', error);
    }
  };

  const toggleBlockUser = async (userId, currentlyBlocked) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/block?blocked=${!currentlyBlocked}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Failed to toggle block status:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" style={{borderTopColor: '#F96F51'}}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg relative">
      <Particles />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 glass-card rounded-xl hover:bg-white/10 transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.3), rgba(249,111,81,0.1))'}}>
                <Shield className="w-6 h-6" style={{color: '#F96F51'}} />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-xs text-white/50">User & Usage Management</p>
              </div>
            </div>
          </div>

          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl hover:bg-white/10 transition-colors"
            data-testid="refresh-data"
          >
            <RefreshCw className="w-4 h-4 text-white/70" />
            <span className="text-white/70">Refresh</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 relative z-10">
        {/* Error banner — never silently render stale zeros */}
        {fetchError && !loading && (
          <div className="mb-6 p-4 rounded-xl border border-red-500/40 bg-red-500/10 flex items-center justify-between gap-4" data-testid="admin-fetch-error">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-white">Couldn't load admin data</p>
                <p className="text-xs text-white/60">{fetchError} — the backend may be busy processing uploads. Retry in a moment.</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              className="px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm font-medium transition-colors flex items-center gap-2"
              data-testid="admin-retry-fetch"
            >
              <RefreshCw className="w-4 h-4" />
              Retry now
            </button>
          </div>
        )}

        {/* Loading skeleton — shown while data is in-flight so users never see lying zeros */}
        {loading && !stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-skeleton">
            {[0,1,2,3].map(i => (
              <div key={i} className="glass-card p-5 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-3 w-20 bg-white/10 rounded animate-pulse" />
                  <div className="w-10 h-10 rounded-xl bg-white/5 animate-pulse" />
                </div>
                <div className="h-10 w-16 bg-white/10 rounded animate-pulse mb-2" />
                <div className="h-3 w-24 bg-white/5 rounded animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {/* Stats Overview */}
        {(!loading || stats) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-1">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Total Users</span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="admin-total-users">
              {stats?.total_users || 0}
            </p>
            <p className="text-xs text-white/40 mt-1">
              +{stats?.weekly_users || 0} this week
            </p>
          </div>

          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-2">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Total Tracks</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.2), rgba(249,111,81,0.05))'}}>
                <Music className="w-5 h-5" style={{color: '#F96F51'}} />
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="admin-total-tracks">
              {stats?.total_tracks || 0}
            </p>
            <p className="text-xs text-white/40 mt-1">
              +{stats?.weekly_tracks || 0} this week
            </p>
          </div>

          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Completed</span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="admin-completed-tracks">
              {stats?.completed_tracks || 0}
            </p>
            <p className="text-xs text-green-400 mt-1">
              {stats?.total_tracks ? Math.round((stats.completed_tracks / stats.total_tracks) * 100) : 0}% success rate
            </p>
          </div>

          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Errors</span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/20 to-red-500/5 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="admin-error-tracks">
              {stats?.error_tracks || 0}
            </p>
            <p className="text-xs text-white/40 mt-1">
              {stats?.processing_tracks || 0} processing
            </p>
          </div>
        </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10">
          {['overview', 'users', 'tracks', 'webhooks'].map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); if (tab === 'webhooks' && !webhookData) fetchWebhooks(); }}
              className={`px-4 py-3 font-medium transition-colors relative ${
                activeTab === tab ? 'text-white' : 'text-white/50 hover:text-white/70'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'overview' && 'Overview'}
              {tab === 'users' && `Users (${users.length})`}
              {tab === 'tracks' && `Recent Tracks (${tracks.length})`}
              {tab === 'webhooks' && 'Webhooks'}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{background: 'linear-gradient(90deg, #F96F51, #ff8a6b)'}} />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Users */}
            <div className="glass-card rounded-2xl p-6 border border-white/10">
              <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{color: '#F96F51'}} />
                Recent Users
              </h3>
              <div className="space-y-3">
                {users.slice(0, 5).map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                    <div>
                      <p className="font-medium text-white">{user.name}</p>
                      <p className="text-sm text-white/50">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold" style={{color: '#F96F51'}}>{user.credits} credits</p>
                      <p className="text-xs text-white/40">{user.company || 'Individual'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Tracks */}
            <div className="glass-card rounded-2xl p-6 border border-white/10">
              <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Music className="w-5 h-5" style={{color: '#F96F51'}} />
                Recent Tracks
              </h3>
              <div className="space-y-3">
                {tracks.slice(0, 5).map(track => (
                  <div key={track.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                    <div>
                      <p className="font-medium text-white truncate max-w-[200px]">{track.filename}</p>
                      <p className="text-sm text-white/50">{track.user_email || 'Unknown'}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${
                      track.status === 'complete' ? 'status-complete' :
                      track.status === 'error' ? 'status-error' : 'status-analyzing'
                    }`}>
                      {track.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="glass-card rounded-2xl overflow-hidden border border-white/10">
            <table className="w-full table-dark" data-testid="users-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">User</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Credits</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Joined</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {users.map(user => (
                  <tr key={user.id} className={`hover:bg-white/5 transition-colors ${user.is_blocked ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {user.is_blocked && (
                          <Ban className="w-4 h-4 text-red-400 flex-shrink-0" />
                        )}
                        <div>
                          <p className="font-medium text-white">{user.name}</p>
                          <p className="text-sm text-white/50">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-white/70">{user.company || '—'}</td>
                    <td className="px-6 py-4">
                      {editingCredits === user.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={newCredits}
                            onChange={(e) => setNewCredits(e.target.value)}
                            className="w-20 px-2 py-1 input-dark rounded text-sm"
                            data-testid={`credits-input-${user.id}`}
                          />
                          <button
                            onClick={() => updateCredits(user.id)}
                            className="text-xs font-medium"
                            style={{color: '#F96F51'}}
                            data-testid={`save-credits-${user.id}`}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => { setEditingCredits(null); setNewCredits(''); }}
                            className="text-xs text-white/50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="font-semibold" style={{color: '#F96F51'}}>{user.credits}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium inline-block w-fit ${
                          user.is_admin ? 'text-white' : 'text-white/50 bg-white/10'
                        }`} style={user.is_admin ? {background: 'linear-gradient(135deg, #F96F51, #ff8a6b)'} : {}}>
                          {user.is_admin ? 'Admin' : 'User'}
                        </span>
                        {user.is_blocked && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 inline-block w-fit">
                            Blocked
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-white/50">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => { setEditingCredits(user.id); setNewCredits(user.credits.toString()); }}
                          className="text-sm hover:underline"
                          style={{color: '#F96F51'}}
                          data-testid={`edit-credits-${user.id}`}
                        >
                          Edit Credits
                        </button>
                        {!user.is_admin && (
                          <button
                            onClick={() => toggleBlockUser(user.id, user.is_blocked)}
                            className={`flex items-center gap-1 text-sm hover:underline ${
                              user.is_blocked ? 'text-green-400' : 'text-red-400'
                            }`}
                            data-testid={`toggle-block-${user.id}`}
                          >
                            {user.is_blocked ? (
                              <>
                                <UserCheck className="w-3.5 h-3.5" />
                                Unblock
                              </>
                            ) : (
                              <>
                                <Ban className="w-3.5 h-3.5" />
                                Block
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'tracks' && (
          <div className="glass-card rounded-2xl overflow-hidden border border-white/10">
            <table className="w-full table-dark" data-testid="admin-tracks-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Track</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">User</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {tracks.map(track => (
                  <tr key={track.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-white truncate max-w-xs">{track.filename}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm text-white">{track.user_name || 'Unknown'}</p>
                        <p className="text-xs text-white/50">{track.user_email || ''}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${
                        track.status === 'complete' ? 'status-complete' :
                        track.status === 'error' ? 'status-error' : 'status-analyzing'
                      }`}>
                        {track.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-white/50">
                      {new Date(track.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'webhooks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Cyanite Webhook Diagnostics</h3>
              <button
                onClick={fetchWebhooks}
                disabled={loadingWebhooks}
                className="px-4 py-2 rounded-lg text-sm text-white/80 bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-50"
                data-testid="refresh-webhooks"
              >
                {loadingWebhooks ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {webhookData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="glass-card rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-white/50 mb-1">Total received</p>
                    <p className="font-heading text-2xl font-bold text-white" data-testid="webhook-total">{webhookData.summary.total}</p>
                  </div>
                  <div className="glass-card rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-white/50 mb-1">HMAC verified</p>
                    <p className="font-heading text-2xl font-bold text-green-400">{webhookData.summary.verified}</p>
                  </div>
                  <div className="glass-card rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-white/50 mb-1">Unverified</p>
                    <p className="font-heading text-2xl font-bold text-yellow-400">{webhookData.summary.unverified}</p>
                  </div>
                  <div className="glass-card rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-white/50 mb-1">Analysis finished</p>
                    <p className="font-heading text-2xl font-bold text-white">{webhookData.summary.analysis_finished}</p>
                  </div>
                  <div className="glass-card rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-white/50 mb-1">Analysis failed</p>
                    <p className="font-heading text-2xl font-bold text-red-400">{webhookData.summary.analysis_failed}</p>
                  </div>
                </div>

                <div className="glass-card rounded-xl p-4 border border-white/10 text-sm text-white/70 flex flex-wrap gap-x-6 gap-y-1">
                  <span><strong className="text-white">Secret configured:</strong> {webhookData.webhook_secret_configured ? 'Yes' : 'No'}</span>
                  <span><strong className="text-white">Endpoint:</strong> {webhookData.webhook_url}</span>
                </div>

                <div className="glass-card rounded-2xl overflow-hidden border border-white/10">
                  <table className="w-full table-dark" data-testid="webhooks-table">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Received</th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Type</th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Cyanite Track ID</th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Verified</th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Track Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {webhookData.recent.length === 0 ? (
                        <tr><td colSpan={5} className="px-6 py-8 text-center text-white/50">No webhooks received yet.</td></tr>
                      ) : webhookData.recent.map(w => (
                        <tr
                          key={w.webhook_id}
                          onClick={() => inspectWebhook(w.webhook_id)}
                          className="hover:bg-white/10 transition-colors cursor-pointer"
                          data-testid={`webhook-row-${w.webhook_id}`}
                          title="Click to inspect payload, headers and signature"
                        >
                          <td className="px-6 py-3 text-sm text-white/70">{w.received_at ? new Date(w.received_at).toLocaleString() : '—'}</td>
                          <td className="px-6 py-3 text-sm text-white font-mono text-xs">{w.type}</td>
                          <td className="px-6 py-3 text-xs text-white/60 font-mono truncate max-w-xs">{w.cyanite_track_id || '—'}</td>
                          <td className="px-6 py-3 text-sm">
                            <span className={w.signature_verified ? 'text-green-400' : 'text-yellow-400'}>
                              {w.signature_verified ? '✓' : '⚠'}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-xs text-white/70">{w.track_status || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {!webhookData && !loadingWebhooks && (
              <div className="glass-card rounded-2xl p-8 text-center border border-white/10">
                <p className="text-white/50">Click Refresh to load recent Cyanite webhook deliveries.</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Webhook detail / signature diagnostics modal */}
      {selectedWebhook && (
        <WebhookInspector
          webhook={selectedWebhook}
          loading={loadingWebhookDetail}
          onClose={() => setSelectedWebhook(null)}
        />
      )}
    </div>
  );
}

// ── Webhook Inspector ───────────────────────────────────────────────────────
// Surfaces the exact payload, headers, raw body and re-verification result for
// one webhook delivery — so admins can diagnose ⚠ verification failures
// (mismatched secret, body mutation by a proxy, missing headers, etc.).
function WebhookInspector({ webhook, loading, onClose }) {
  const reverifyColor = {
    ok: 'text-green-400',
    failed: 'text-red-400',
    error: 'text-amber-400',
  }[webhook?.reverify_status] || 'text-white/50';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
      onClick={onClose}
      data-testid="webhook-inspector"
    >
      <div
        className="glass-card rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 glass border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Webhook Inspector</h3>
            <p className="text-xs text-white/50 font-mono mt-0.5">{webhook.webhook_id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition"
            data-testid="webhook-inspector-close"
          >✕</button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5" style={{maxHeight: 'calc(92vh - 73px)'}}>
          {loading || webhook._loading ? (
            <p className="text-white/50">Loading webhook details…</p>
          ) : webhook._error ? (
            <p className="text-red-400">Failed to load: {webhook._error}</p>
          ) : (
            <>
              {/* Verification banner */}
              <div className="grid md:grid-cols-3 gap-3">
                <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <p className="text-xs text-white/40 uppercase tracking-wider">Verified at receipt</p>
                  <p className={`text-lg font-semibold mt-1 ${webhook.signature_verified_at_receipt ? 'text-green-400' : 'text-amber-400'}`}>
                    {webhook.signature_verified_at_receipt ? '✓ Yes' : '⚠ No'}
                  </p>
                  <p className="text-[10px] text-white/40 mt-1">The check that ran when the webhook arrived.</p>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <p className="text-xs text-white/40 uppercase tracking-wider">Re-verify (now, no time window)</p>
                  <p className={`text-lg font-semibold mt-1 ${reverifyColor}`}>
                    {webhook.reverify_status === 'ok' ? '✓ ok' :
                     webhook.reverify_status === 'failed' ? '✗ signature mismatch' :
                     webhook.reverify_status === 'error' ? '⚠ error' :
                     webhook.reverify_status === 'skipped' ? '— skipped' : '— skipped'}
                  </p>
                  {webhook.reverify_detail && (
                    <p className="text-[11px] text-white/60 font-mono mt-1 break-all">{webhook.reverify_detail}</p>
                  )}
                  {webhook.reverify_error && (
                    <p className="text-[11px] text-amber-400 font-mono mt-1 break-all">{webhook.reverify_error}</p>
                  )}
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <p className="text-xs text-white/40 uppercase tracking-wider">Secret fingerprint</p>
                  <p className="text-sm font-mono mt-1 text-white/80">{webhook.secret_fingerprint || '— not configured'}</p>
                  <p className="text-[10px] text-white/40 mt-1">Compare against the value Cyanite gave you.</p>
                  {webhook.raw_body_len !== null && webhook.raw_body_len !== undefined && (
                    <p className="text-[10px] text-white/40 mt-1">Body bytes received: <span className="text-white/70 font-mono">{webhook.raw_body_len}</span></p>
                  )}
                </div>
              </div>

              {/* Track linkage */}
              {webhook.track && (
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Linked Track</p>
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-white/40">Filename:</span> <span className="text-white">{webhook.track.filename}</span></div>
                    <div><span className="text-white/40">Status:</span> <span className="text-white">{webhook.track.status}</span></div>
                    <div><span className="text-white/40">Tier:</span> <span className="text-white">{webhook.track.processing_tier ?? '—'}</span></div>
                  </div>
                </div>
              )}

              {/* Headers */}
              <div>
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Headers</p>
                <div className="rounded-xl bg-black/40 border border-white/10 p-4 font-mono text-xs text-white/80 max-h-48 overflow-y-auto">
                  {Object.entries(webhook.headers || {}).length === 0 ? (
                    <p className="text-white/40">No headers recorded.</p>
                  ) : Object.entries(webhook.headers || {}).map(([k, v]) => (
                    <div key={k} className="mb-1 break-all">
                      <span className="text-primary">{k}:</span> <span className="text-white/70">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payload */}
              <div>
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Payload (parsed JSON)</p>
                <pre className="rounded-xl bg-black/40 border border-white/10 p-4 font-mono text-xs text-white/80 overflow-x-auto max-h-72">
{JSON.stringify(webhook.payload || {}, null, 2)}
                </pre>
              </div>

              {/* Raw body */}
              {webhook.raw_body_excerpt && (
                <div>
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Raw body (excerpt)</p>
                  <pre className="rounded-xl bg-black/40 border border-white/10 p-4 font-mono text-[11px] text-white/70 overflow-x-auto max-h-48 whitespace-pre-wrap">
{webhook.raw_body_excerpt}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
